import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
// PERF-LCP-4 — sonner ships ~32 kB raw / ~11 kB gzip and is never
// needed on first paint. Lazy-load so it stays out of the entry chunk.
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, IdleLogoutWatcher } from "@/lib/auth-context";
import { PageviewTracker } from "@/components/analytics/PageviewTracker";

// PERF-LCP-4 — gates, role guards, and layout shells statically import
// useMyEvents / useRole / the confetti barrel, which transitively pull
// the supabase client + admin nav surface into whatever chunk they live
// in. Keeping them eager dragged supabase + ~70 kB of admin chrome into
// the entry. Lazy-loading is safe — they only render inside their own
// route subtrees (/setup/*, /admin/*, /superadmin/*) which are
// themselves already lazy.
const AdminGate = lazy(() => import("@/components/RouteGates").then((m) => ({ default: m.AdminGate })));
const SetupGate = lazy(() => import("@/components/RouteGates").then((m) => ({ default: m.SetupGate })));
const RequireRole = lazy(() => import("@/components/RequireRole").then((m) => ({ default: m.RequireRole })));
const AdminLayout = lazy(() => import("@/layouts/AdminLayout").then((m) => ({ default: m.AdminLayout })));
const SuperAdminLayout = lazy(() => import("@/layouts/SuperAdminLayout").then((m) => ({ default: m.SuperAdminLayout })));

// Landing is eager — it's the LCP route. Everything else is lazy so the
// initial JS payload on `/` stays minimal (PERF-LCP-2).
import Landing from "./pages/Landing";
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Contact = lazy(() => import("./pages/Contact"));
const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Auth/setup ancillaries — lazy. Visited rarely, never on first paint.
const AuthConfirm = lazy(() => import("./pages/AuthConfirm"));
const AuthCheckEmail = lazy(() => import("./pages/AuthCheckEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SetupEvent = lazy(() => import("./pages/SetupEvent"));
const SetupPayment = lazy(() => import("./pages/SetupPayment"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));

// Admin dashboard — lazy. Behind AdminGate, never reached without auth.
const AdminOverview = lazy(() => import("./pages/admin/Overview"));
const AdminGuests = lazy(() => import("./pages/admin/Guests"));
const AdminTables = lazy(() => import("./pages/admin/Tables"));
const AdminGallery = lazy(() => import("./pages/admin/Gallery"));
const AdminSlideshow = lazy(() => import("./pages/admin/Slideshow"));
const AdminQR = lazy(() => import("./pages/admin/QR"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminHelp = lazy(() => import("./pages/admin/Help"));
const AdminPrintables = lazy(() => import("./pages/admin/Printables"));

// Super-admin — lazy. Tiny audience, gated by RequireRole.
const SuperAdminOverview = lazy(() => import("./pages/superadmin/Overview"));
const SuperAdminCustomers = lazy(() => import("./pages/superadmin/Customers"));
const SuperAdminPayments = lazy(() => import("./pages/superadmin/Payments"));
const SuperAdminCodes = lazy(() => import("./pages/superadmin/Codes"));
const SuperAdminAffiliates = lazy(() => import("./pages/superadmin/Affiliates"));
const SuperAdminAudit = lazy(() => import("./pages/superadmin/Audit"));
const SuperAdminView = lazy(() => import("./pages/superadmin/View"));
const SuperAdminPastWeddings = lazy(() => import("./pages/superadmin/PastWeddings"));
const SuperAdminKeepsakes = lazy(() => import("./pages/superadmin/Keepsakes"));
const SuperAdminFeedback = lazy(() => import("./pages/superadmin/Feedback"));
const SuperAdminFeedbackEvent = lazy(() => import("./pages/superadmin/FeedbackEvent"));

// Guest flow — lazy. Reached via QR scan, not from main bundle traffic.
const GuestWelcome = lazy(() => import("./pages/guest/Welcome"));
const GuestHowItWorks = lazy(() => import("./pages/guest/HowItWorks"));
const GuestConfirm = lazy(() => import("./pages/guest/Confirm"));
const GuestChallenge = lazy(() => import("./pages/guest/Challenge"));
const GuestUpload = lazy(() => import("./pages/guest/Upload"));
const GuestSubmitted = lazy(() => import("./pages/guest/Submitted"));
const GuestGallery = lazy(() => import("./pages/guest/Gallery"));
const EventEnded = lazy(() =>
  import("./pages/guest/Placeholders").then((m) => ({ default: m.EventEnded })),
);
const PublicSlideshow = lazy(() => import("./pages/PublicSlideshow"));

const queryClient = new QueryClient();

// Suspense fallback matches existing inline-loader convention used by
// RouteGates (see src/components/RouteGates.tsx) — quiet, on-brand, no flash.
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center font-body text-text-muted">
    Loading…
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
          <Toaster />
          <Suspense fallback={null}>
            <Sonner />
          </Suspense>
          <BrowserRouter>
            <PageviewTracker />
            <IdleLogoutWatcher />
            <a href="#main" className="skip-to-content">Skip to main content</a>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/confirm" element={<AuthConfirm />} />
                <Route path="/auth/check-email" element={<AuthCheckEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                <Route element={<SetupGate />}>
                  <Route path="/setup/event" element={<SetupEvent />} />
                  <Route path="/setup/payment" element={<SetupPayment />} />
                  <Route path="/setup/return" element={<CheckoutReturn />} />
                </Route>

                <Route
                  path="/admin"
                  element={
                    <AdminGate>
                      <AdminLayout />
                    </AdminGate>
                  }
                >
                  <Route index element={<AdminOverview />} />
                  <Route path="guests" element={<AdminGuests />} />
                  <Route path="tables" element={<AdminTables />} />
                  <Route path="gallery" element={<AdminGallery />} />
                  <Route path="slideshow" element={<AdminSlideshow />} />
                  <Route path="qr" element={<AdminQR />} />
                  <Route
                    path="printables"
                    element={
                      <RequireRole role="admin">
                        <AdminPrintables />
                      </RequireRole>
                    }
                  />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="help" element={<AdminHelp />} />
                </Route>

                <Route
                  path="/superadmin"
                  element={
                    <RequireRole role="admin">
                      <SuperAdminLayout />
                    </RequireRole>
                  }
                >
                  <Route index element={<SuperAdminOverview />} />
                  <Route path="customers" element={<SuperAdminCustomers />} />
                  <Route path="past-weddings" element={<SuperAdminPastWeddings />} />
                  <Route path="payments" element={<SuperAdminPayments />} />
                  <Route path="codes" element={<SuperAdminCodes />} />
                  <Route path="affiliates" element={<SuperAdminAffiliates />} />
                  <Route path="audit" element={<SuperAdminAudit />} />
                  <Route path="keepsakes" element={<SuperAdminKeepsakes />} />
                  <Route path="feedback" element={<SuperAdminFeedback />} />
                  <Route path="feedback/:eventId" element={<SuperAdminFeedbackEvent />} />
                  <Route path="view/:eventId" element={<SuperAdminView />} />
                </Route>

                <Route path="/invite/:token" element={<InviteAccept />} />

                <Route path="/event/ended" element={<EventEnded />} />
                <Route path="/event/:slug" element={<GuestWelcome />} />
                <Route path="/event/:slug/how-it-works" element={<GuestHowItWorks />} />
                <Route path="/event/:slug/confirm/:guestId" element={<GuestConfirm />} />
                <Route path="/event/:slug/challenge/:guestId" element={<GuestChallenge />} />
                <Route path="/event/:slug/upload/:guestId" element={<GuestUpload />} />
                <Route path="/event/:slug/submitted/:guestId" element={<GuestSubmitted />} />
                <Route path="/event/:slug/gallery" element={<GuestGallery />} />
                <Route path="/s/:token" element={<PublicSlideshow />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
